import { useState } from 'react';
import type { UseQueryResult } from 'react-query';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useCurrentSite } from '@hotjar/site';
import type { Site } from '@hotjar/site';
import { checkGTMSetup } from '@hotjar/webapp/domains/activation/gtm/api';

const MAX_RETRIES = 30;
const GTM_CHECK = 'gtmCheckSetup';

interface GTMCheckResult {
  finished: boolean;
  success: boolean;
  message: string;
}

export const useGTMCheck = (): UseQueryResult<GTMCheckResult, Error> => {
  const { data: currentSite } = useCurrentSite();
  const siteId = currentSite?.id;
  const [retries, setRetries] = useState(0);
  const { t } = useTranslation();
  const DEFAULT_MESSAGE = t('activation:gtm-loader.waiting-for-google-services');

  return useQuery([GTM_CHECK, { siteId }], () => checkGTMSetup(currentSite as Site), {
    enabled: currentSite !== undefined && retries < MAX_RETRIES,
    refetchInterval: 2000,
    refetchIntervalInBackground: true,
    placeholderData: {
      success: false,
      finished: false,
      message: DEFAULT_MESSAGE,
    },
    onSuccess: () => setRetries(retries + 1),
    select: ({ success }) => {
      let message = DEFAULT_MESSAGE;
      if (retries > 10 && retries < 20) {
        message = t('activation:gtm-loader.still-waiting');
      } else if (retries > 5) {
        message = t('activation:gtm-loader.please-wait');
      }
      return { success, finished: retries === MAX_RETRIES, message };
    },
  });
};
